<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import { getInboxIconByType } from 'dashboard/helper/inbox';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import LiveChatCampaignDetails from './LiveChatCampaignDetails.vue';
import SMSCampaignDetails from './SMSCampaignDetails.vue';

const props = defineProps({
  title: {
    type: String,
    default: '',
  },
  message: {
    type: String,
    default: '',
  },
  isLiveChatType: {
    type: Boolean,
    default: false,
  },
  isEnabled: {
    type: Boolean,
    default: false,
  },
  status: {
    type: String,
    default: '',
  },
  sender: {
    type: Object,
    default: null,
  },
  inbox: {
    type: Object,
    default: null,
  },
  scheduledAt: {
    type: Number,
    default: 0,
  },
  canToggle: {
    type: Boolean,
    default: false,
  },
  isToggling: {
    type: Boolean,
    default: false,
  },
  hasActiveDispatches: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'edit',
  'delete',
  'viewLogs',
  'toggleEnabled',
  'duplicate',
  'dispatchRemaining',
]);

const { t } = useI18n();

const STATUS_COMPLETED = 'completed';

const { formatMessage } = useMessageFormatter();

const isOneOffCompleted = computed(() => props.status === STATUS_COMPLETED);

const isActive = computed(() => {
  if (props.isLiveChatType) {
    return props.isEnabled;
  }

  return !isOneOffCompleted.value && props.isEnabled;
});

const canToggleEnabled = computed(
  () =>
    props.canToggle &&
    !props.isLiveChatType &&
    // Show while running, OR while paused (resume), OR whenever there is still a
    // queued/paused dispatch backlog — even if the status was flipped to
    // completed. Only a truly-finished one-off (no live logs) hides the button.
    (!isOneOffCompleted.value ||
      !props.isEnabled ||
      props.hasActiveDispatches)
);

const statusTextColor = computed(() => ({
  'text-n-teal-11': isActive.value,
  'text-n-slate-12': !isActive.value,
}));

const campaignStatus = computed(() => {
  if (props.isLiveChatType) {
    return props.isEnabled
      ? t('CAMPAIGN.LIVE_CHAT.CARD.STATUS.ENABLED')
      : t('CAMPAIGN.LIVE_CHAT.CARD.STATUS.DISABLED');
  }

  if (isOneOffCompleted.value) {
    return t('CAMPAIGN.SMS.CARD.STATUS.COMPLETED');
  }

  return props.isEnabled
    ? t('CAMPAIGN.SMS.CARD.STATUS.SCHEDULED')
    : t('CAMPAIGN.SMS.CARD.STATUS.PAUSED');
});

const toggleButtonLabel = computed(() =>
  props.isEnabled
    ? t('CAMPAIGN.SMS.CARD.BUTTONS.PAUSE')
    : t('CAMPAIGN.SMS.CARD.BUTTONS.ACTIVATE')
);

const toggleButtonIcon = computed(() =>
  props.isEnabled ? 'i-lucide-pause' : 'i-lucide-play'
);

const toggleButtonColor = computed(() => (props.isEnabled ? 'amber' : 'teal'));

const inboxName = computed(() => props.inbox?.name || '');

const inboxIcon = computed(() => {
  const { medium, channel_type: type } = props.inbox;
  return getInboxIconByType(type, medium);
});
</script>

<template>
  <CardLayout layout="row">
    <div class="flex flex-col items-start justify-between flex-1 min-w-0 gap-2">
      <div class="flex justify-between gap-3 w-fit">
        <span
          class="text-base font-medium capitalize text-n-slate-12 line-clamp-1"
        >
          {{ title }}
        </span>
        <span
          class="text-xs font-medium inline-flex items-center h-6 px-2 py-0.5 rounded-md bg-n-alpha-2"
          :class="statusTextColor"
        >
          {{ campaignStatus }}
        </span>
      </div>
      <div
        v-dompurify-html="formatMessage(message, false, false, false)"
        class="text-sm text-n-slate-11 line-clamp-1 [&>p]:mb-0 h-6"
      />
      <div class="flex items-center w-full h-6 gap-2 overflow-hidden">
        <LiveChatCampaignDetails
          v-if="isLiveChatType"
          :sender="sender"
          :inbox-name="inboxName"
          :inbox-icon="inboxIcon"
        />
        <SMSCampaignDetails
          v-else
          :inbox-name="inboxName"
          :inbox-icon="inboxIcon"
          :scheduled-at="scheduledAt"
        />
      </div>
    </div>
    <div class="flex items-center justify-end gap-2">
      <Button
        v-if="canToggleEnabled"
        variant="faded"
        size="sm"
        :color="toggleButtonColor"
        :icon="toggleButtonIcon"
        :label="toggleButtonLabel"
        :is-loading="isToggling"
        @click="emit('toggleEnabled')"
      />
      <Button
        v-if="!isLiveChatType"
        variant="faded"
        size="sm"
        color="teal"
        icon="i-lucide-list-plus"
        :title="'Disparar para os contatos ainda não enviados'"
        aria-label="Disparar restantes"
        @click="emit('dispatchRemaining')"
      />
      <Button
        v-if="!isLiveChatType"
        variant="faded"
        size="sm"
        color="slate"
        icon="i-lucide-scroll-text"
        @click="emit('viewLogs')"
      />
      <Button
        variant="faded"
        size="sm"
        color="slate"
        icon="i-lucide-sliders-vertical"
        @click="emit('edit')"
      />
      <Button
        v-if="!isLiveChatType"
        variant="faded"
        size="sm"
        color="slate"
        icon="i-lucide-copy"
        @click="emit('duplicate')"
      />
      <Button
        variant="faded"
        color="ruby"
        size="sm"
        icon="i-lucide-trash"
        @click="emit('delete')"
      />
    </div>
  </CardLayout>
</template>
